import { Box, Typography, TextField, Button, InputAdornment, Divider, RadioGroup, FormControlLabel, Radio, Paper, Checkbox, FormGroup } from "@mui/material";
import Tooltip from "../components/ui/tooltip";
import React, { useState } from "react";

import Topbar from "../components/topbar";
import { useToast } from "../providers/toaster-provider";
import { useUser } from "../providers/user-provider";
import { useSettings } from "../providers/settings-provider";
import { usePermissions } from "../providers/permissions-provider";
import Toggle from "../components/ui/toggle";
import Dropdown from "../components/ui/dropdown";

import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';
import SectionWrapper from "../components/section";

const ProvisionSettings: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    // Page visibility (the route's PermissionGuard) is gated on the new
    // settings_provisions resource, but saving still goes through the
    // shared InvoiceSettings PUT endpoint, which the backend hardcodes to
    // require settings_invoice:update (routes/invoice-settings.js) - so the
    // edit gate here has to match that, not settings_provisions, or a role
    // granted one but not the other would see an enabled Save button that
    // 403s. Revisit once/if this moves off the InvoiceSettings table.
    const isReadOnly = !can('settings_invoice', 'update');
    const { showSuccess, showError } = useToast();
    const { formatDate } = useSettings();

    // Stored on the InvoiceSettings row keyed by the fixed literal statusId
    // 'invoice' (see provision-aging-service.js) - a holdover from when this
    // lived on the Invoice Settings page, not moved to its own table yet.
    // '' (nothing selected) = feature off, the required default.
    const [provisionAgingAction, setProvisionAgingAction] = useState<'' | 'delete' | 'push_fixed' | 'push_configurable'>('');
    // Default = apply as soon as a provision's date has passed, no offset
    // (threshold 0). Custom = a configurable, possibly-negative day offset
    // from today (see provisionAgingThresholdDays), revealed only when this
    // is on.
    const [provisionAgingUseCustomThreshold, setProvisionAgingUseCustomThreshold] = useState(false);
    // Which source(s) of provision the configured action applies to - default
    // (true/true/false) matches the original hardcoded scope from before
    // this was selectable, and is also what an entity that's never touched
    // this control gets (see resolveProvisionAgingScope, provision-aging-service.js).
    const [provisionAgingApplyToInvoice, setProvisionAgingApplyToInvoice] = useState(true);
    const [provisionAgingApplyToRecurring, setProvisionAgingApplyToRecurring] = useState(true);
    const [provisionAgingApplyToOneTime, setProvisionAgingApplyToOneTime] = useState(false);
    // Payroll-generated provisions used to fall silently under "recurring"
    // (a payroll template's own type is a payment cycle, never 'one-time') -
    // default true, matching that legacy behavior, same as recurring's own
    // default (see resolveProvisionAgingScope, provision-aging-service.js).
    const [provisionAgingApplyToPayroll, setProvisionAgingApplyToPayroll] = useState(true);
    // Always a non-negative integer (digits-only, enforced on input) - the
    // signed offset the backend expects is recomposed from this + direction
    // below. Keeping the UI state unsigned avoids the "type a negative
    // number to mean 'before'" trick that was confusing users.
    const [provisionAgingThresholdDays, setProvisionAgingThresholdDays] = useState('15');
    const [provisionAgingThresholdDirection, setProvisionAgingThresholdDirection] = useState<'before' | 'after'>('after');
    const [provisionAgingPushDays, setProvisionAgingPushDays] = useState('30');
    const [provisionAgingBadDebtRatio, setProvisionAgingBadDebtRatio] = useState('0');
    // Applies to both push_fixed and push_configurable - whether a provision
    // that got pushed forward by this job still counts toward the running
    // balance/cashflow calculation. Default true (include) matches existing
    // behavior for entities that haven't touched this yet.
    const [provisionAgingIncludeInBalance, setProvisionAgingIncludeInBalance] = useState(true);
    const [provisionAgingHasChanges, setProvisionAgingHasChanges] = React.useState(false);
    const [provisionAgingOriginalValues, setProvisionAgingOriginalValues] = React.useState({
        action: '' as '' | 'delete' | 'push_fixed' | 'push_configurable',
        useCustomThreshold: false,
        thresholdDays: '15',
        thresholdDirection: 'after' as 'before' | 'after',
        pushDays: '30',
        badDebtRatio: '0',
        includeInBalance: true,
        applyToInvoice: true,
        applyToRecurring: true,
        applyToOneTime: false,
        applyToPayroll: true,
    });

    // Read-only info about the login-triggered run (see
    // triggerProvisionAgingIfNeeded, provision-aging-service.js) - not part
    // of the editable form, just surfaced so admins can see whether/when it
    // last ran without having to check the Transactions page for evidence.
    const [provisionAgingRunInfo, setProvisionAgingRunInfo] = React.useState<{
        status: 'running' | 'completed' | 'failed' | null;
        startedAt: string | null;
        completedAt: string | null;
        summary: { deletedCount?: number; pushedCount?: number; errorCount?: number; error?: string } | null;
    }>({ status: null, startedAt: null, completedAt: null, summary: null });

    React.useEffect(() => {
        const hasAnyChanges =
            provisionAgingAction !== provisionAgingOriginalValues.action ||
            provisionAgingUseCustomThreshold !== provisionAgingOriginalValues.useCustomThreshold ||
            provisionAgingThresholdDays !== provisionAgingOriginalValues.thresholdDays ||
            provisionAgingThresholdDirection !== provisionAgingOriginalValues.thresholdDirection ||
            provisionAgingPushDays !== provisionAgingOriginalValues.pushDays ||
            provisionAgingBadDebtRatio !== provisionAgingOriginalValues.badDebtRatio ||
            provisionAgingIncludeInBalance !== provisionAgingOriginalValues.includeInBalance ||
            provisionAgingApplyToInvoice !== provisionAgingOriginalValues.applyToInvoice ||
            provisionAgingApplyToRecurring !== provisionAgingOriginalValues.applyToRecurring ||
            provisionAgingApplyToOneTime !== provisionAgingOriginalValues.applyToOneTime ||
            provisionAgingApplyToPayroll !== provisionAgingOriginalValues.applyToPayroll;
        setProvisionAgingHasChanges(hasAnyChanges);
    }, [provisionAgingAction, provisionAgingUseCustomThreshold, provisionAgingThresholdDays, provisionAgingThresholdDirection, provisionAgingPushDays, provisionAgingBadDebtRatio, provisionAgingIncludeInBalance, provisionAgingApplyToInvoice, provisionAgingApplyToRecurring, provisionAgingApplyToOneTime, provisionAgingApplyToPayroll, provisionAgingOriginalValues]);

    const [fetchProvisionAgingSettings, setFetchProvisionAgingSettings] = useApi();

    React.useEffect(() => {
        document.title = "FineAnts | Provision Settings";
    }, []);

    React.useEffect(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoiceSettings.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            { param: 'statusId', value: 'invoice' },
        ]);
        setFetchProvisionAgingSettings({ url, method });
    }, [user?.entityId, setFetchProvisionAgingSettings]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchProvisionAgingSettings;
        if (data && !isLoading) {
            const action = (data.provisionAgingAction || '') as '' | 'delete' | 'push_fixed' | 'push_configurable';
            // A stored threshold (even 0) means "custom" was explicitly
            // chosen; no stored value means "default" (no offset).
            const useCustomThreshold = data.provisionAgingThresholdDays !== null && data.provisionAgingThresholdDays !== undefined;
            const rawThreshold = parseInt(data.provisionAgingThresholdDays, 10) || 0;
            const thresholdDays = useCustomThreshold ? Math.abs(rawThreshold).toString() : '15';
            const thresholdDirection: 'before' | 'after' = rawThreshold < 0 ? 'before' : 'after';
            const pushDays = data.provisionAgingPushDays?.toString() || '30';
            const badDebtRatio = data.provisionAgingBadDebtRatio?.toString() || '0';
            // null/undefined (never configured) defaults to true, same as
            // the backend's resolveIncludePushedForwardInBalance.
            const includeInBalance = data.provisionAgingIncludeInBalance !== false;
            // All three unset means this entity configured aging before the
            // checkboxes existed - keep the original hardcoded scope (invoice
            // + recurring) rather than defaulting to "nothing selected",
            // mirroring resolveProvisionAgingScope on the backend.
            const neverConfiguredScope = data.provisionAgingApplyToInvoice == null && data.provisionAgingApplyToRecurring == null && data.provisionAgingApplyToOneTime == null;
            const applyToInvoice = neverConfiguredScope ? true : !!data.provisionAgingApplyToInvoice;
            const applyToRecurring = neverConfiguredScope ? true : !!data.provisionAgingApplyToRecurring;
            const applyToOneTime = neverConfiguredScope ? false : !!data.provisionAgingApplyToOneTime;
            // Payroll defaults to whatever recurring resolved to until the
            // entity explicitly saves its own value for it - it used to fall
            // silently under "recurring", so this covers both an entity that
            // never configured aging at all AND one that configured the
            // original three but has never seen this checkbox yet (mirrors
            // resolveProvisionAgingScope on the backend).
            const applyToPayroll = data.provisionAgingApplyToPayroll != null ? !!data.provisionAgingApplyToPayroll : applyToRecurring;
            setProvisionAgingAction(action);
            setProvisionAgingUseCustomThreshold(useCustomThreshold);
            setProvisionAgingThresholdDays(thresholdDays);
            setProvisionAgingThresholdDirection(thresholdDirection);
            setProvisionAgingPushDays(pushDays);
            setProvisionAgingBadDebtRatio(badDebtRatio);
            setProvisionAgingIncludeInBalance(includeInBalance);
            setProvisionAgingApplyToInvoice(applyToInvoice);
            setProvisionAgingApplyToRecurring(applyToRecurring);
            setProvisionAgingApplyToOneTime(applyToOneTime);
            setProvisionAgingApplyToPayroll(applyToPayroll);
            setProvisionAgingOriginalValues({ action, useCustomThreshold, thresholdDays, thresholdDirection, pushDays, badDebtRatio, includeInBalance, applyToInvoice, applyToRecurring, applyToOneTime, applyToPayroll });
            setProvisionAgingHasChanges(false);

            let summary = null;
            if (data.provisionAgingRunSummary) {
                try {
                    summary = JSON.parse(data.provisionAgingRunSummary);
                } catch {
                    summary = null;
                }
            }
            setProvisionAgingRunInfo({
                status: data.provisionAgingRunStatus || null,
                startedAt: data.provisionAgingRunStartedAt || null,
                completedAt: data.provisionAgingRunCompletedAt || null,
                summary,
            });
        }
        if (error) {
            showError('Failed to fetch provision aging settings');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchProvisionAgingSettings, showError]);

    const handleSaveProvisionAging = async () => {
        if (!user?.entityId) {
            showError('Entity ID is missing');
            return;
        }
        try {
            const { url, method } = buildApiRequestConfig(routes.invoiceSettings.createOrUpdate, [
                { param: 'entityId', value: user.entityId }
            ]);

            const formData = new FormData();
            formData.append('statusId', 'invoice');
            formData.append('provisionAgingAction', provisionAgingAction);
            const signedThresholdDays = (provisionAgingThresholdDirection === 'before' ? -1 : 1) * (parseInt(provisionAgingThresholdDays, 10) || 0);
            formData.append('provisionAgingThresholdDays', provisionAgingUseCustomThreshold ? String(signedThresholdDays) : '');
            formData.append('provisionAgingPushDays', provisionAgingAction === 'push_configurable' ? provisionAgingPushDays : '');
            formData.append('provisionAgingBadDebtRatio', provisionAgingAction === 'push_configurable' ? provisionAgingBadDebtRatio : '');
            formData.append('provisionAgingIncludeInBalance', String(provisionAgingIncludeInBalance));
            formData.append('provisionAgingApplyToInvoice', String(provisionAgingApplyToInvoice));
            formData.append('provisionAgingApplyToRecurring', String(provisionAgingApplyToRecurring));
            formData.append('provisionAgingApplyToOneTime', String(provisionAgingApplyToOneTime));
            formData.append('provisionAgingApplyToPayroll', String(provisionAgingApplyToPayroll));

            const token = document.cookie
                .split('; ')
                .find(row => row.startsWith('token='))
                ?.split('=')[1];
            const headers: HeadersInit = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(url, { method, body: formData, headers });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update provision aging settings');
            }

            showSuccess('Provision aging settings updated successfully');
            setProvisionAgingOriginalValues({
                action: provisionAgingAction,
                useCustomThreshold: provisionAgingUseCustomThreshold,
                thresholdDays: provisionAgingThresholdDays,
                thresholdDirection: provisionAgingThresholdDirection,
                pushDays: provisionAgingPushDays,
                badDebtRatio: provisionAgingBadDebtRatio,
                includeInBalance: provisionAgingIncludeInBalance,
                applyToInvoice: provisionAgingApplyToInvoice,
                applyToRecurring: provisionAgingApplyToRecurring,
                applyToOneTime: provisionAgingApplyToOneTime,
                applyToPayroll: provisionAgingApplyToPayroll,
            });
            setProvisionAgingHasChanges(false);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to update provision aging settings';
            showError(errorMessage);
        }
    };

    // Concrete "today -> this date" preview so the cutoff's purpose (which
    // provisions the next run would actually catch) is obvious at a glance,
    // instead of making users work it out from the day count + before/after
    // wording themselves. Mirrors the backend's own math exactly, including
    // its floor: "has passed" (threshold >= 0, i.e. default or "after") can
    // never include a provision dated TODAY - due today isn't overdue yet -
    // even at a 0 offset. "before" (threshold < 0) is deliberately
    // inclusive of today, since its whole point is catching provisions
    // early. See the matching comment in processEntityProvisionAging,
    // provision-aging-service.js - this must stay in sync with that fix.
    const thresholdMagnitude = Math.abs(parseInt(provisionAgingThresholdDays, 10) || 0);
    const signedThresholdForPreview = provisionAgingUseCustomThreshold
        ? (provisionAgingThresholdDirection === 'before' ? -thresholdMagnitude : thresholdMagnitude)
        : 0;
    const isPastMode = signedThresholdForPreview >= 0;
    const todayForPreview = new Date();
    const cutoffForPreview = new Date(todayForPreview);
    cutoffForPreview.setDate(cutoffForPreview.getDate() - signedThresholdForPreview);
    if (isPastMode && cutoffForPreview.getTime() >= todayForPreview.getTime()) {
        cutoffForPreview.setDate(cutoffForPreview.getDate() - 1);
    }
    const dayAfterCutoffForPreview = new Date(cutoffForPreview);
    dayAfterCutoffForPreview.setDate(dayAfterCutoffForPreview.getDate() + 1);
    const sameDay = cutoffForPreview.getTime() === todayForPreview.getTime();

    // Schematic (not to-scale) timeline positions - a 1-day offset and a
    // 365-day offset need to look equally readable, which an actual-scale
    // axis can't do. Today and the cutoff just swap sides depending on
    // which one actually falls earlier; when they're the same day there's a
    // single midpoint marker.
    const cutoffIsEarlier = cutoffForPreview.getTime() < todayForPreview.getTime();
    const cutoffPct = sameDay ? 50 : cutoffIsEarlier ? 32 : 68;
    const todayPct = sameDay ? 50 : cutoffIsEarlier ? 68 : 32;

    const whenHeadline = sameDay
        ? `Today is ${formatDate(todayForPreview)}. Any unpaid provision dated on or before today is treated - as soon as its date has passed.`
        : !isPastMode
            ? `Today is ${formatDate(todayForPreview)}. Any unpaid provision dated on or before ${formatDate(cutoffForPreview)} is treated - that covers everything due within the next ${thresholdMagnitude} day${thresholdMagnitude === 1 ? '' : 's'}, even ones not due yet.`
            : `Today is ${formatDate(todayForPreview)}. Any unpaid provision dated on or before ${formatDate(cutoffForPreview)} is treated - its date has already passed${signedThresholdForPreview > 0 ? ` by at least ${thresholdMagnitude} day${thresholdMagnitude === 1 ? '' : 's'}` : ''}. A provision dated today is never treated yet - it isn't overdue.`;
    const whenBoundaryExample = sameDay
        ? `For example: a provision dated today is treated next run; one dated tomorrow isn't - not yet.`
        : `For example: a provision dated ${formatDate(cutoffForPreview)} (right at the cutoff) is treated next run; one dated ${formatDate(dayAfterCutoffForPreview)} isn't - not yet.`;
    const whenCadenceNote = `This check runs automatically, at most once a day (the next time someone logs in). Since it's always measured from today, the cutoff date above moves forward with it, one day at a time.`;

    return (
        <div style={{ width: '100%' }}>
            <Topbar title="Provision Settings"/>
            <div style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SectionWrapper title="Provision Aging">
                    <Box maxWidth={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Provision Aging</Typography>
                            <Tooltip message="What happens to unpaid provisions - generated from invoices or from recurring templates - once their date has passed (or a custom number of days before/after). Runs automatically, at most once a day, the next time someone logs in." />
                        </Box>

                        {provisionAgingAction && (() => {
                            const { status, startedAt, completedAt, summary } = provisionAgingRunInfo;
                            let text: string;
                            if (status === 'running') {
                                text = `Currently running (started ${startedAt ? formatDate(startedAt, true) : 'moments ago'})...`;
                            } else if (status === 'completed') {
                                const parts = [
                                    `${summary?.deletedCount ?? 0} deleted`,
                                    `${summary?.pushedCount ?? 0} pushed forward`,
                                ];
                                if (summary?.errorCount) parts.push(`${summary.errorCount} error${summary.errorCount === 1 ? '' : 's'}`);
                                text = `Last ran ${completedAt ? formatDate(completedAt, true) : ''} - ${parts.join(', ')}.`;
                            } else if (status === 'failed') {
                                text = `Last attempt (${completedAt ? formatDate(completedAt, true) : ''}) failed${summary?.error ? `: ${summary.error}` : '.'}`;
                            } else {
                                text = "Hasn't run yet - runs automatically the next time someone logs in.";
                            }
                            return (
                                <Typography sx={{ fontSize: 12, color: status === 'failed' ? '#d1180aff' : 'var(--color-text-muted)', mb: 2 }}>
                                    {text}
                                </Typography>
                            );
                        })()}

                        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>What it applies to</Typography>
                        <FormGroup sx={{ mb: provisionAgingApplyToInvoice || provisionAgingApplyToRecurring || provisionAgingApplyToOneTime || provisionAgingApplyToPayroll ? 2 : 0.5 }}>
                            <FormControlLabel
                                control={<Checkbox size="small" checked={provisionAgingApplyToInvoice} onChange={(e) => setProvisionAgingApplyToInvoice(e.target.checked)} disabled={isReadOnly} />}
                                label={<Typography sx={{ fontSize: 13 }}>Invoice-generated provisions</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox size="small" checked={provisionAgingApplyToRecurring} onChange={(e) => setProvisionAgingApplyToRecurring(e.target.checked)} disabled={isReadOnly} />}
                                label={<Typography sx={{ fontSize: 13 }}>Recurring provisions</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox size="small" checked={provisionAgingApplyToOneTime} onChange={(e) => setProvisionAgingApplyToOneTime(e.target.checked)} disabled={isReadOnly} />}
                                label={<Typography sx={{ fontSize: 13 }}>One-time provisions</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox size="small" checked={provisionAgingApplyToPayroll} onChange={(e) => setProvisionAgingApplyToPayroll(e.target.checked)} disabled={isReadOnly} />}
                                label={<Typography sx={{ fontSize: 13 }}>Payroll-generated provisions</Typography>}
                            />
                        </FormGroup>
                        {!provisionAgingApplyToInvoice && !provisionAgingApplyToRecurring && !provisionAgingApplyToOneTime && !provisionAgingApplyToPayroll && (
                            <Typography sx={{ fontSize: 12, color: '#d1180aff', fontStyle: 'italic', mb: 2 }}>
                                Nothing is selected, so provision aging won't apply to anything even once configured below.
                            </Typography>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>When it gets applied</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 40, mb: provisionAgingUseCustomThreshold ? 0.5 : 1 }}>
                            <Toggle size="small" checked={provisionAgingUseCustomThreshold} onChange={() => setProvisionAgingUseCustomThreshold(prev => !prev)} disabled={isReadOnly} />
                            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Use a custom offset instead of applying as soon as a provision's date has passed</Typography>
                        </Box>
                        {provisionAgingUseCustomThreshold && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Apply</Typography>
                                <TextField
                                    value={provisionAgingThresholdDays}
                                    onChange={(e) => setProvisionAgingThresholdDays(e.target.value.replace(/[^0-9]/g, ''))}
                                    size="small" sx={{ width: 64, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    inputProps={{ style: { textAlign: 'center' }, inputMode: 'numeric' }}
                                    disabled={isReadOnly}
                                />
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>days</Typography>
                                <Dropdown
                                    options={[
                                        { value: 'after', label: 'after' },
                                        { value: 'before', label: 'before' },
                                    ]}
                                    value={provisionAgingThresholdDirection}
                                    onChange={(val) => setProvisionAgingThresholdDirection(val as 'before' | 'after')}
                                    width={100}
                                    height={36}
                                    disabled={isReadOnly}
                                />
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                                    a provision's date {provisionAgingThresholdDirection === 'before' ? 'arrives' : 'has passed'}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{
                            border: '1px solid rgba(0, 182, 155, 0.3)',
                            background: 'rgba(0, 182, 155, 0.05)',
                            borderRadius: 2,
                            p: 2,
                            mb: 2,
                        }}>
                            <Typography sx={{ fontSize: 12.5, color: 'var(--color-default-text)', mb: 2 }}>
                                {whenHeadline}
                            </Typography>

                            {/* Schematic (not to-scale) timeline - see cutoffPct/todayPct above for why. */}
                            <Box sx={{ position: 'relative', height: sameDay ? 76 : 128, mx: 1 }}>
                                <Box sx={{ position: 'absolute', top: 30, left: 0, right: 0, height: 8, borderRadius: 4, background: '#e0e0e0', overflow: 'hidden' }}>
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${cutoffPct}%`, background: 'var(--color-primary)' }} />
                                </Box>
                                {/* Zone labels always show their own boundary date too - not just via the
                                    marker(s) above the track - so "what's treated" is legible on its own. */}
                                <Box sx={{ position: 'absolute', top: 42, left: 0, width: `${cutoffPct}%`, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--color-primary)', letterSpacing: 0.3, lineHeight: 1.4 }}>
                                        TREATED
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'var(--color-primary)', lineHeight: 1.3 }}>
                                        up to {formatDate(cutoffForPreview)}
                                    </Typography>
                                </Box>
                                <Box sx={{ position: 'absolute', top: 42, left: `${cutoffPct}%`, width: `${100 - cutoffPct}%`, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: 0.3, lineHeight: 1.4 }}>
                                        NOT YET
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)', lineHeight: 1.3 }}>
                                        from {formatDate(dayAfterCutoffForPreview)}
                                    </Typography>
                                </Box>

                                <Box sx={{ position: 'absolute', left: `${cutoffPct}%`, top: 0, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 140 }}>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--color-primary)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                        CUTOFF{sameDay ? ' = TODAY' : ''}
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'var(--color-primary)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                        {formatDate(cutoffForPreview)}
                                    </Typography>
                                    <Box sx={{ width: 2, height: 8, background: 'var(--color-primary)', mt: 0.25 }} />
                                </Box>

                                {!sameDay && (
                                    <Box sx={{ position: 'absolute', left: `${todayPct}%`, top: 76, transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', width: 140 }}>
                                        <Box sx={{ width: 2, height: 8, background: '#616161' }} />
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--color-default-text)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                            TODAY
                                        </Typography>
                                        <Typography sx={{ fontSize: 10, color: 'var(--color-text-muted)', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                            {formatDate(todayForPreview)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Typography sx={{ fontSize: 12, color: 'var(--color-default-text)', mt: 1 }}>
                                {whenBoundaryExample}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)', fontStyle: 'italic', mt: 1 }}>
                                {whenCadenceNote}
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>How it's treated</Typography>
                        <RadioGroup
                            value={provisionAgingAction}
                            onChange={(e) => setProvisionAgingAction(e.target.value as '' | 'delete' | 'push_fixed' | 'push_configurable')}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                        >
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, cursor: isReadOnly ? 'default' : 'pointer', borderRadius: 2,
                                    border: provisionAgingAction === 'delete' ? '2px solid var(--color-primary)' : '1px solid #e0e0e0' }}
                                onClick={() => !isReadOnly && setProvisionAgingAction('delete')}
                            >
                                <FormControlLabel value="delete" control={<Radio disabled={isReadOnly} />} sx={{ m: 0, alignItems: 'flex-start' }}
                                    label={
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Delete the provision</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Removes the aged, unpaid provision.</Typography>
                                        </Box>
                                    }
                                />
                            </Paper>

                            <Paper
                                variant="outlined"
                                sx={{ p: 2, cursor: isReadOnly ? 'default' : 'pointer', borderRadius: 2,
                                    border: provisionAgingAction === 'push_fixed' ? '2px solid var(--color-primary)' : '1px solid #e0e0e0' }}
                                onClick={() => !isReadOnly && setProvisionAgingAction('push_fixed')}
                            >
                                <FormControlLabel value="push_fixed" control={<Radio disabled={isReadOnly} />} sx={{ m: 0, alignItems: 'flex-start' }}
                                    label={
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Push 1 month forward</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Moves the provision's date to 1 calendar month from today.</Typography>
                                        </Box>
                                    }
                                />
                            </Paper>

                            <Paper
                                variant="outlined"
                                sx={{ p: 2, cursor: isReadOnly ? 'default' : 'pointer', borderRadius: 2,
                                    border: provisionAgingAction === 'push_configurable' ? '2px solid var(--color-primary)' : '1px solid #e0e0e0' }}
                                onClick={() => !isReadOnly && setProvisionAgingAction('push_configurable')}
                            >
                                <FormControlLabel value="push_configurable" control={<Radio disabled={isReadOnly} />} sx={{ m: 0, alignItems: 'flex-start' }}
                                    label={
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>Push forward with custom controls</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Choose the number of days to push forward, and optionally reduce the provision's amount by a bad debt ratio (applied once).</Typography>
                                        </Box>
                                    }
                                />
                                {provisionAgingAction === 'push_configurable' && (
                                    <Box sx={{ display: 'flex', gap: 3, mt: 1.5, ml: 4, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Push forward by</Typography>
                                            <TextField
                                                value={provisionAgingPushDays}
                                                onChange={(e) => setProvisionAgingPushDays(e.target.value)}
                                                size="small" sx={{ width: 64, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                inputProps={{ style: { textAlign: 'center' } }}
                                                disabled={isReadOnly}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>days</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Bad debt ratio</Typography>
                                            <TextField
                                                value={provisionAgingBadDebtRatio}
                                                onChange={(e) => setProvisionAgingBadDebtRatio(e.target.value)}
                                                size="small" sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                                inputProps={{ style: { textAlign: 'center' } }}
                                                disabled={isReadOnly}
                                                onClick={(e) => e.stopPropagation()}
                                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        </RadioGroup>

                        {(provisionAgingAction === 'push_fixed' || provisionAgingAction === 'push_configurable') && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 40, mt: 1.5 }}>
                                <Toggle size="small" checked={provisionAgingIncludeInBalance} onChange={() => setProvisionAgingIncludeInBalance(prev => !prev)} disabled={isReadOnly} />
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Include pushed-forward provisions in the balance calculation</Typography>
                            </Box>
                        )}

                        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', mt: 1.5 }}>
                            Applies only to unpaid (unlinked) provisions of whichever type(s) are checked above - provisions already matched to a real transaction are never touched. This is opt-in: leave nothing selected below to keep this off entirely. Runs automatically, at most once a day, the next time someone logs in.
                        </Typography>

                        <Divider sx={{ mt: 2, mb: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="contained" onClick={handleSaveProvisionAging} disabled={!provisionAgingHasChanges || isReadOnly}
                                sx={{ background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, textTransform: 'none', px: 4, '&:hover': { background: 'var(--color-primary-hover)' } }}>
                                Save Changes
                            </Button>
                        </Box>
                    </Box>
                </SectionWrapper>
            </div>
        </div>
    );
};

export default ProvisionSettings;
